import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Award, Sparkles, CheckCircle2, ArrowRight, Loader2, Globe, FileText, ChevronRight, X
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../config/api';
import Logo from '../components/Logo';
import { useSEO } from '../hooks/useSEO';
import { auth, googleProvider } from '../services/firebase';
import { signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { apiService } from '../services/apiService';

const PartnershipPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useSEO({
    title: 'Partnership Program',
    description: 'Daftar kemitraan program influencer dan komunitas CatatCrypto untuk mendapatkan komisi recurring hingga 30%.',
    keywords: 'partnership, community partner, influencer affiliate, recurring commission'
  });

  // Check if user is already a partner and redirect to referral page
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/partnership/dashboard`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        if (res.ok) {
          const data = await res.json();
          // If they have any active partnership status (approved, pending, or rejected), redirect them!
          if (data.success || data.application_status === 'pending' || data.application_status === 'rejected') {
            navigate('/referral');
          }
        }
      } catch (err) {
        console.error('Failed to check partnership status:', err);
      }
    };

    checkStatus();
  }, [navigate]);

  const [partnerType, setPartnerType] = useState<'influencer' | 'community'>('community');
  const [communityName, setCommunityName] = useState('');
  const [communityLink, setCommunityLink] = useState('');
  const [applyReason, setApplyReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [authMode, setAuthMode] = useState<'register' | 'login'>('register');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authName, setAuthName] = useState('');
  const [authError, setAuthError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  const validateAuthFields = (type?: 'login' | 'register' | 'google') => {
    const errors: { [key: string]: string } = {};
    if (type === 'google') return true;

    if (type === 'register' && !authName.trim()) {
      errors.authName = 'Nama lengkap wajib diisi.';
    }

    if (!authEmail.trim()) {
      errors.authEmail = 'Kolom email wajib diisi.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(authEmail)) {
      errors.authEmail = 'Format email tidak valid (Contoh: nama@domain.com).';
    }

    if (!authPassword) {
      errors.authPassword = 'Password wajib diisi.';
    } else if (authPassword.length < 8) {
      errors.authPassword = 'Password minimal 8 karakter.';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateFormFields = () => {
    const errors: { [key: string]: string } = {};

    if (!authEmail.trim()) {
      errors.authEmail = 'Email akun CatatCrypto wajib diisi.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(authEmail)) {
      errors.authEmail = 'Format email tidak valid (Contoh: nama@domain.com).';
    }

    if (!communityName.trim()) {
      errors.communityName = partnerType === 'community' ? 'Nama komunitas wajib diisi.' : 'Nama influencer/brand wajib diisi.';
    }

    if (!communityLink.trim()) {
      errors.communityLink = 'Tautan komunitas wajib diisi.';
    } else if (!communityLink.startsWith('https://')) {
      errors.communityLink = 'Tautan harus diawali dengan https:// (Contoh: https://t.me/channel)';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAuthAndSubmit = async (e?: React.FormEvent, type?: 'login' | 'register' | 'google') => {
    if (e) e.preventDefault();
    setAuthError('');
    
    if (type !== 'google' && !validateAuthFields(type)) {
      return;
    }

    setIsAuthenticating(true);

    try {
      let idToken = '';
      let emailVal = authEmail;
      let nameVal = authName;

      if (type === 'google') {
        const userCredential = await signInWithPopup(auth, googleProvider);
        idToken = await userCredential.user.getIdToken();
        emailVal = userCredential.user.email || '';
        nameVal = userCredential.user.displayName || '';
      } else if (type === 'register') {
        // Verify email via check-email endpoint first
        const checkRes = await fetch(`${API_BASE_URL}/api/check-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ email: authEmail })
        });
        const checkData = await checkRes.json();
        if (!checkRes.ok) {
          setAuthError(checkData.message || 'Domain email tidak dapat digunakan.');
          setIsAuthenticating(false);
          return;
        }

        const userCredential = await createUserWithEmailAndPassword(auth, authEmail, authPassword);
        idToken = await userCredential.user.getIdToken();
      } else {
        const userCredential = await signInWithEmailAndPassword(auth, authEmail, authPassword);
        idToken = await userCredential.user.getIdToken();
      }

      // 2. Login to backend
      const loginData = await apiService.loginWithFirebase(idToken);
      localStorage.setItem('auth_token', loginData.token);
      localStorage.setItem('trader_name', loginData.user.name || nameVal);

      // 3. Submit application matching backend DTO keys
      const payload = {
        type: partnerType,
        email: emailVal || authEmail,
        community_name: communityName,
        community_link: communityLink,
        reason: applyReason,
        notification_pref: 'email'
      };

      const applyRes = await fetch(`${API_BASE_URL}/api/partnership/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${loginData.token}`
        },
        body: JSON.stringify(payload)
      });
      const applyData = await applyRes.json();
      if (applyRes.ok && applyData.success) {
        setSuccess(true);
        setShowAuthForm(false);
      } else {
        setAuthError(applyData.message || 'Gagal mengirim pengajuan kemitraan.');
      }
    } catch (err: any) {
      console.error(err);
      let errMsg = 'Terjadi kesalahan sistem. Silakan coba lagi.';
      if (err.code === 'auth/email-already-in-use') {
        errMsg = 'Email sudah terdaftar. Silakan pilih "Masuk di sini".';
      } else if (err.code === 'auth/weak-password') {
        errMsg = 'Password terlalu lemah. Gunakan minimal 8 karakter.';
      } else if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        errMsg = 'Email atau password yang Anda masukkan salah.';
      } else if (err.code === 'auth/user-not-found') {
        errMsg = 'Akun tidak ditemukan. Silakan buat akun baru terlebih dahulu.';
      }
      setAuthError(errMsg);
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateFormFields()) {
      return;
    }

    const payload = {
      type: partnerType,
      email: authEmail,
      community_name: communityName,
      community_link: communityLink,
      reason: applyReason,
      notification_pref: 'email'
    };

    const token = localStorage.getItem('auth_token');
    if (!token) {
      setShowAuthForm(true);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/partnership/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess(true);
      } else {
        setError(data.message || 'Gagal mengirim pengajuan kemitraan.');
      }
    } catch {
      setError('Koneksi server gagal. Pastikan jaringan internet Anda aktif.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-4 relative overflow-hidden select-none">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-red-600/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="w-full max-w-xl relative z-10 space-y-6">
        <div className="flex justify-center">
          <Logo size="lg" centered />
        </div>

        <AnimatePresence mode="wait">
          {success ? (
            <motion.div 
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-neutral-950 border border-neutral-800 rounded-3xl p-8 text-center space-y-6 shadow-2xl"
            >
              <div className="w-20 h-20 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center mx-auto text-green-500">
                <CheckCircle2 size={40} />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-black text-white">
                  {t('referral.successTitle') || 'Pengajuan Dikirim!'}
                </h2>
                <p className="text-neutral-400 text-sm font-sans leading-relaxed">
                  {t('referral.pendingSub') || 'Pengajuan Kemitraan Anda berhasil dikirim ke tim marketing. Kami akan meninjau kelayakan komunitas Anda dan mengirim notifikasi persetujuan.'}
                </p>
              </div>
              <button 
                onClick={() => navigate('/referral')}
                className="w-full py-3.5 bg-red-600 hover:bg-red-500 text-white font-bold rounded-xl transition-all shadow-lg flex items-center justify-center gap-1.5 cursor-pointer font-sans"
              >
                <span>{t('partnership.goToDashboard') || 'Masuk ke Dashboard'}</span>
                <ArrowRight size={16} />
              </button>
            </motion.div>
          ) : showAuthForm ? (
            <motion.div 
              key="auth"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-neutral-950 border border-neutral-800 rounded-3xl p-6 md:p-8 space-y-6 shadow-2xl font-sans"
            >
              <div className="flex justify-between items-center">
                <button 
                  onClick={() => setShowAuthForm(false)}
                  className="text-xs font-bold text-neutral-500 hover:text-white transition-colors"
                >
                  &larr; {t('referral.back') || 'Kembali'}
                </button>
                <span className="text-[10px] font-black uppercase tracking-widest text-red-500">
                  {authMode === 'register' ? 'Daftar Akun Baru' : 'Masuk Akun'}
                </span>
              </div>

              <div className="space-y-2">
                <h2 className="text-xl font-black text-white">
                  {authMode === 'register' ? 'Buat Akun Anda' : 'Masuk ke Akun Anda'}
                </h2>
                <p className="text-xs text-neutral-400">
                  Silakan masuk atau daftar terlebih dahulu untuk mengirim pengajuan kemitraan Anda secara langsung.
                </p>
              </div>

              {authError && (
                <div className="p-3 bg-red-950/20 border border-red-900/30 rounded-xl text-red-500 text-xs font-bold">
                  {authError}
                </div>
              )}

              <form onSubmit={(e) => handleAuthAndSubmit(e, authMode)} className="space-y-4">
                {authMode === 'register' && (
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">
                      Nama Lengkap
                    </label>
                    <input 
                      type="text"
                      placeholder="Nama Lengkap Anda"
                      className={`w-full bg-neutral-900 border ${fieldErrors.authName ? 'border-red-500 bg-red-950/10' : 'border-neutral-800 focus:border-red-600'} rounded-xl py-2.5 px-4 text-sm outline-none text-white transition-colors`}
                      value={authName}
                      onChange={(e) => {
                        setAuthName(e.target.value);
                        if (fieldErrors.authName) setFieldErrors(prev => ({ ...prev, authName: '' }));
                      }}
                    />
                    {fieldErrors.authName && (
                      <p className="text-red-500 text-[11px] font-sans font-bold pl-1 mt-1 flex items-center gap-1">
                        <span>⚠️</span> {fieldErrors.authName}
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">
                    Email
                  </label>
                  <input 
                    type="email"
                    placeholder="email@domain.com"
                    className={`w-full bg-neutral-900 border ${fieldErrors.authEmail ? 'border-red-500 bg-red-950/10' : 'border-neutral-800 focus:border-red-600'} rounded-xl py-2.5 px-4 text-sm outline-none text-white transition-colors`}
                    value={authEmail}
                    onChange={(e) => {
                      setAuthEmail(e.target.value);
                      if (fieldErrors.authEmail) setFieldErrors(prev => ({ ...prev, authEmail: '' }));
                    }}
                  />
                  {fieldErrors.authEmail && (
                    <p className="text-red-500 text-[11px] font-sans font-bold pl-1 mt-1 flex items-center gap-1">
                      <span>⚠️</span> {fieldErrors.authEmail}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">
                    Password
                  </label>
                  <input 
                    type="password"
                    placeholder="••••••••"
                    className={`w-full bg-neutral-900 border ${fieldErrors.authPassword ? 'border-red-500 bg-red-950/10' : 'border-neutral-800 focus:border-red-600'} rounded-xl py-2.5 px-4 text-sm outline-none text-white transition-colors`}
                    value={authPassword}
                    onChange={(e) => {
                      setAuthPassword(e.target.value);
                      if (fieldErrors.authPassword) setFieldErrors(prev => ({ ...prev, authPassword: '' }));
                    }}
                  />
                  {fieldErrors.authPassword && (
                    <p className="text-red-500 text-[11px] font-sans font-bold pl-1 mt-1 flex items-center gap-1">
                      <span>⚠️</span> {fieldErrors.authPassword}
                    </p>
                  )}
                </div>

                <button 
                  type="submit"
                  disabled={isAuthenticating}
                  className="w-full py-3 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-bold rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer text-sm"
                >
                  {isAuthenticating ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Memproses...</span>
                    </>
                  ) : (
                    <span>{authMode === 'register' ? 'Daftar & Kirim Pengajuan' : 'Masuk & Kirim Pengajuan'}</span>
                  )}
                </button>
              </form>

              <div className="relative flex items-center justify-center my-4">
                <div className="absolute w-full h-[1px] bg-neutral-800" />
                <span className="relative bg-neutral-950 px-3 text-[10px] font-bold text-neutral-500 uppercase tracking-wider">
                  Atau
                </span>
              </div>

              <button 
                type="button"
                disabled={isAuthenticating}
                onClick={() => handleAuthAndSubmit(undefined, 'google')}
                className="w-full py-3 bg-neutral-900 border border-neutral-800 hover:border-neutral-700 disabled:opacity-50 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2.5 cursor-pointer text-sm font-sans"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.77c-.98.66-2.23 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                </svg>
                <span>Masuk dengan Google</span>
              </button>

              <p className="text-center text-xs text-neutral-500 font-sans">
                {authMode === 'register' ? 'Sudah punya akun?' : 'Belum punya akun?'}{' '}
                <button 
                  onClick={() => setAuthMode(authMode === 'register' ? 'login' : 'register')}
                  className="text-red-500 hover:underline font-bold"
                >
                  {authMode === 'register' ? 'Masuk di sini' : 'Daftar di sini'}
                </button>
              </p>
            </motion.div>
          ) : (
            <motion.div 
              key="form"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              className="bg-neutral-950 border border-neutral-800 rounded-3xl p-6 md:p-8 space-y-6 shadow-2xl font-sans"
            >
              <div className="flex justify-between items-center">
                <button 
                  onClick={() => navigate(-1)}
                  className="text-xs font-bold text-neutral-500 hover:text-white transition-colors"
                >
                  &larr; {t('referral.back') || 'Kembali'}
                </button>
                <span className="text-[9px] font-black uppercase tracking-widest text-red-500">
                  {partnerType === 'community' ? 'Community Partner' : 'Influencer Program'}
                </span>
              </div>

              <div className="space-y-2">
                <h2 className="text-xl font-black text-white">
                  {t('referral.formTitle') || 'Formulir Pendaftaran Mitra'}
                </h2>
                <p className="text-xs text-neutral-400">
                  {t('referral.formSub') || 'Harap lengkapi kolom berikut. Tim kami akan meninjau dan mengirimkan detail persetujuan melalui email Anda.'}
                </p>
              </div>

              {error && (
                <div className="p-3 bg-red-950/20 border border-red-900/30 rounded-xl text-red-500 text-xs font-bold">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Tipe Kemitraan Switch */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1 block">
                    {t('referral.programType') || 'Pilihan Program'}
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setPartnerType('influencer')}
                      className={`p-4 rounded-2xl border text-left transition-all ${partnerType === 'influencer' ? 'bg-red-600/10 border-red-600 text-white' : 'bg-neutral-900/20 border-neutral-800 text-neutral-400 hover:border-neutral-700'}`}
                    >
                      <Award size={18} className="text-red-500 mb-2" />
                      <h4 className="text-xs font-black uppercase tracking-wide">Influencer</h4>
                      <p className="text-[9px] text-neutral-500 mt-1 font-sans leading-snug">Personal trader & media publisher</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPartnerType('community')}
                      className={`p-4 rounded-2xl border text-left transition-all ${partnerType === 'community' ? 'bg-purple-600/10 border-purple-600 text-white' : 'bg-neutral-900/20 border-neutral-800 text-neutral-400 hover:border-neutral-700'}`}
                    >
                      <Users size={18} className="text-purple-400 mb-2" />
                      <h4 className="text-xs font-black uppercase tracking-wide">Community</h4>
                      <p className="text-[9px] text-neutral-500 mt-1 font-sans leading-snug">Signal groups & regional guilds</p>
                    </button>
                  </div>
                </div>

                {/* Email Akun CatatCrypto */}
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">
                    Email Akun CatatCrypto
                  </label>
                  <input 
                    type="email"
                    placeholder="email@domain.com"
                    className={`w-full bg-neutral-900 border ${fieldErrors.authEmail ? 'border-red-500 bg-red-950/10' : 'border-neutral-800 focus:border-red-600'} rounded-xl py-3 px-4 text-xs outline-none text-white transition-colors`}
                    value={authEmail}
                    onChange={(e) => {
                      setAuthEmail(e.target.value);
                      if (fieldErrors.authEmail) setFieldErrors(prev => ({ ...prev, authEmail: '' }));
                    }}
                  />
                  {fieldErrors.authEmail && (
                    <p className="text-red-500 text-[11px] font-sans font-bold pl-1 mt-1 flex items-center gap-1">
                      <span>⚠️</span> {fieldErrors.authEmail}
                    </p>
                  )}
                </div>

                {/* Name */}
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">
                    {partnerType === 'community' ? t('referral.communityName') || 'Nama Komunitas' : t('referral.influencerName') || 'Nama Influencer / Brand'}
                  </label>
                  <input 
                    type="text"
                    placeholder={partnerType === 'community' ? 'Crypto Cuan Group' : 'Nama Akun / Channel Anda'}
                    className={`w-full bg-neutral-900 border ${fieldErrors.communityName ? 'border-red-500 bg-red-950/10' : 'border-neutral-800 focus:border-red-600'} rounded-xl py-3 px-4 text-xs outline-none text-white transition-colors font-bold`}
                    value={communityName}
                    onChange={(e) => {
                      setCommunityName(e.target.value);
                      if (fieldErrors.communityName) setFieldErrors(prev => ({ ...prev, communityName: '' }));
                    }}
                  />
                  {fieldErrors.communityName && (
                    <p className="text-red-500 text-[11px] font-sans font-bold pl-1 mt-1 flex items-center gap-1">
                      <span>⚠️</span> {fieldErrors.communityName}
                    </p>
                  )}
                </div>

                {/* Community Link */}
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">
                    {t('referral.communityLink') || 'Tautan Komunitas / Channel (Wajib https://)'}
                  </label>
                  <input 
                    type="url"
                    placeholder="https://t.me/... atau https://youtube.com/..."
                    className={`w-full bg-neutral-900 border ${fieldErrors.communityLink ? 'border-red-500 bg-red-950/10' : 'border-neutral-800 focus:border-red-600'} rounded-xl py-3 px-4 text-xs font-mono outline-none text-white transition-colors`}
                    value={communityLink}
                    onChange={(e) => {
                      setCommunityLink(e.target.value);
                      if (fieldErrors.communityLink) setFieldErrors(prev => ({ ...prev, communityLink: '' }));
                    }}
                  />
                  {fieldErrors.communityLink && (
                    <p className="text-red-500 text-[11px] font-sans font-bold pl-1 mt-1 flex items-center gap-1">
                      <span>⚠️</span> {fieldErrors.communityLink}
                    </p>
                  )}
                </div>

                {/* Apply Reason */}
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">
                    {t('referral.applyReason') || 'Alasan Kemitraan (Opsional)'}
                  </label>
                  <textarea 
                    placeholder="Tuliskan alasan mengapa Anda tertarik bermitra..."
                    rows={4}
                    className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-3 px-4 text-xs focus:border-red-600 outline-none text-white transition-colors resize-none"
                    value={applyReason}
                    onChange={(e) => setApplyReason(e.target.value)}
                  />
                </div>

                {/* Info Notifikasi Email */}
                <div className="p-3.5 bg-neutral-900/60 border border-neutral-800 rounded-xl text-neutral-400 text-xs font-sans leading-relaxed">
                  <p className="flex items-start gap-2">
                    <Sparkles size={16} className="text-red-500 shrink-0 mt-0.5" />
                    <span>Info status persetujuan pengajuan kemitraan akan dikirimkan melalui email terdaftar di atas.</span>
                  </p>
                </div>

                <button 
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-red-600 hover:bg-red-500 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 mt-6 font-sans text-xs uppercase tracking-widest"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : t('referral.submit') || 'Kirim Pengajuan Kemitraan'}
                </button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PartnershipPage;
